import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import projectService from '../../services/projectService';
import './ProjectDetail.css';
import { customDateFormatShort } from '../../config/utils';
import {
  FiEdit,
  FiTrash2,
  FiCalendar,
  FiFlag,
  FiTrendingUp,
  FiDollarSign,
  FiCheckSquare,
  FiPlus,
  FiUser,
  FiCheckCircle,
} from 'react-icons/fi';
import { FaIdBadge } from 'react-icons/fa6';

const ProjectDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchProject();
    fetchTasks();
  }, [id]);

  const fetchProject = async () => {
    try {
      setLoading(true);
      const data = await projectService.getProjectById(id);
      setProject(data.project);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch project');
    } finally {
      setLoading(false);
    }
  };

  const fetchTasks = async () => {
    try {
      const data = await projectService.getProjectTasks(id);
      setTasks(data.tasks);
    } catch (err) {
      console.error('Failed to fetch tasks:', err);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this project?')) {
      return;
    }

    try {
      await projectService.deleteProject(id);
      navigate('/app/projects');
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to delete project');
    }
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      'Planning': 'badge-secondary',
      'In Progress': 'badge-primary',
      'On Hold': 'badge-warning',
      'Completed': 'badge-success',
      'Cancelled': 'badge-danger'
    };
    return statusColors[status] || 'badge-secondary';
  };

  const getPriorityBadge = (priority) => {
    const priorityColors = {
      'Low': 'badge-info',
      'Medium': 'badge-warning',
      'High': 'badge-danger',
      'Critical': 'badge-danger-dark'
    };
    return priorityColors[priority] || 'badge-secondary';
  };

  if (loading) {
    return <div className="loading">Loading project...</div>;
  }

  if (error) {
    return (
      <div className="error-container">
        <div className="alert alert-danger">{error}</div>
        <Link to="/app/projects" className="btn btn-primary">Back to Projects</Link>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="error-container">
        <div className="alert alert-warning">Project not found</div>
        <Link to="/app/projects" className="btn btn-primary">Back to Projects</Link>
      </div>
    );
  }

  const budgetRemaining = project.budget_allocated - project.budget_spent;
  const budgetPercentage = project.budget_allocated > 0
    ? (project.budget_spent / project.budget_allocated * 100).toFixed(1)
    : 0;

  return (
    <div className="project-detail-container">
      <div className="project-detail-header">
        <div className="header-left">
          <Link to="/app/projects" className="back-link">
            <i className="fas fa-arrow-left"></i> Back to Projects
          </Link>
          {/* <div className="project-title-section">
            <h1>{project.name}</h1>
            <span className="project-code">{project.code}</span>
          </div> */}
        </div>
        {/* <div className="header-actions">
          <Link to={`/app/projects/${id}/edit`} className="btn btn-outline-primary">
            <i className="fas fa-edit"></i> Edit
          </Link>
          <button onClick={handleDelete} className="btn btn-outline-danger">
            <i className="fas fa-trash"></i> Delete
          </button>
        </div> */}
      </div>




      <div className="min-h-screen bg-gray-50 p-4 md:p-6">
        <div className="mx-auto max-w-7xl space-y-6">

          {/* Hero Header */}
          <div className="relative overflow-hidden p-6 md:p-8 text-primary-800">
            <div className="absolute inset-0" />
            <div className="relative flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">

              <div>
              
                <h1 className="text-3xl md:text-4xl font-bold">
                  {project.name}
                </h1>

                <p className="mt-2 text-sm md:text-base">
                  Project Code: {project.code}
                </p>
              </div>

              <div className="flex items-center gap-3">
                <Link
                  to={`/app/projects/${id}/edit`}
                  className="btn btn-outline-primary"
                >
                  <FiEdit />
                </Link>

                <button
                  onClick={handleDelete}
                  className="btn btn-outline-primary"
                >
                  <FiTrash2 />
                </button>
              </div>
            </div>
          </div>

          {/* KPI Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-5">

            <div className="bg-white rounded-2xl border border-gray-200 p-5 shadow-sm hover:shadow-md transition">
              <div className="flex items-center gap-3">
                <div className="p-3 rounded-xl bg-blue-100 text-blue-600">
                  <FiCheckCircle className="text-xl" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Status</p>
                  <p className="mt-1 font-semibold text-gray-900">{project.status}</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl border border-gray-200 p-5 shadow-sm hover:shadow-md transition">
              <div className="flex items-center gap-3">
                <div className="p-3 rounded-xl bg-amber-100 text-amber-600">
                  <FiTrendingUp className="text-xl" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Progress</p>
                  <p className="mt-1 font-semibold text-gray-900">{project.progress_percentage}%</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl border border-gray-200 p-5 shadow-sm hover:shadow-md transition">
              <div className="flex items-center gap-3">
                <div className="p-3 rounded-xl bg-emerald-100 text-emerald-600">
                  <FiCalendar className="text-xl" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Start Date</p>
                  <p className="mt-1 font-semibold text-gray-900">
                    {project.start_date ? new Date(project.start_date).toLocaleDateString() : '-'}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl border border-gray-200 p-5 shadow-sm hover:shadow-md transition">
              <div className="flex items-center gap-3">
                <div className="p-3 rounded-xl bg-rose-100 text-rose-600">
                  <FiCalendar className="text-xl" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">End Date</p>
                  <p className="mt-1 font-semibold text-gray-900">
                    {project.end_date ? new Date(project.end_date).toLocaleDateString() : '-'}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Main Grid */}
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">

            {/* Left Column */}
            <div className="xl:col-span-2 space-y-6">

              {/* Project Manager */}
              <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm">
                <div className="flex items-center gap-2 mb-5">
                  <FiUser className="text-indigo-600" />
                  <h2 className="text-lg font-semibold text-gray-900">Project Manager</h2>
                </div>

                <div className="flex items-center gap-4 rounded-2xl border border-gray-100 bg-gray-50 p-4">
                  {project?.project_manager?.avatar_url ? (
                    <img
                      src={project.project_manager.avatar_url}
                      alt={project.project_manager.first_name || 'User'}
                      className="w-14 h-14 rounded-full object-cover ring-4 ring-white shadow"
                    />
                  ) : (
                    <div className="w-14 h-14 rounded-full bg-indigo-600 text-white flex items-center justify-center text-lg font-bold ring-4 ring-white shadow">
                      {project?.project_manager?.first_name?.charAt(0)?.toUpperCase() || 'U'}
                    </div>
                  )}

                  <div>
                    <p className="font-semibold text-gray-900">
                      {project?.project_manager?.first_name || 'Unknown User'}
                    </p>
                    <p className="text-sm text-gray-500">Project Owner</p>
                  </div>
                </div>
              </div>

              {/* Progress */}
              <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold text-gray-900">Project Progress</h2>
                  <span className="text-sm font-semibold text-indigo-600">
                    {project.progress_percentage}%
                  </span>
                </div>

                <div className="w-full h-3 rounded-full bg-gray-100 overflow-hidden">
                  <div
                    className="h-full rounded-full bg-blue-500 transition-all duration-700"
                    style={{ width: `${project.progress_percentage}%` }}
                  />
                </div>

                <div className="mt-3 flex items-center justify-between text-sm text-gray-500">
                  <span>Started</span>
                  <span>Target completion</span>
                </div>
              </div>

              {/* Description */}
              <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Project Description</h2>

                {project.description ? (
                  <div
                    className="prose max-w-none prose-indigo text-gray-700"
                    dangerouslySetInnerHTML={{ __html: project.description }}
                  />
                ) : (
                  <p className="text-gray-500">No description provided.</p>
                )}
              </div>
            </div>

            {/* Right Column - Budget */}
            <div className="space-y-6">

              <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm">
                <div className="flex items-center gap-2 mb-5">
                  <FiDollarSign className="text-emerald-600" />
                  <h2 className="text-lg font-semibold text-gray-900">Budget Overview</h2>
                </div>

                <div className="space-y-4">

                  <div className="rounded-xl bg-emerald-50 border border-emerald-100 p-4">
                    <p className="text-sm text-emerald-700">Allocated</p>
                    <p className="text-2xl font-bold text-emerald-900">
                      ${project.budget_allocated?.toLocaleString() || '0'}
                    </p>
                  </div>

                  <div className="rounded-xl bg-amber-50 border border-amber-100 p-4">
                    <p className="text-sm text-amber-700">Spent</p>
                    <p className="text-2xl font-bold text-amber-900">
                      ${project.budget_spent?.toLocaleString() || '0'}
                    </p>
                  </div>

                  

                  <div className={`rounded-xl border p-4 ${budgetRemaining < 0
                      ? 'bg-blue-50 border-red-100'
                      : 'bg-blue-50 border-indigo-100'
                    }`}>
                    <p className={`text-sm ${budgetRemaining < 0 ? 'text-red-700' : 'text-blue-700'
                      }`}>
                      Remaining
                    </p>

                    <p className={`text-2xl font-bold ${budgetRemaining < 0 ? 'text-red-900' : 'text-blue-900'
                      }`}>
                      ${budgetRemaining?.toLocaleString() || '0'}
                    </p>
                  </div>
                </div>

                {project.budget_allocated > 0 && (
                  <div className="mt-6">
                    <div className="flex items-center justify-between text-sm mb-2">
                      <span className="text-gray-600">Budget used</span>
                      <span className="font-semibold text-gray-900">{budgetPercentage}%</span>
                    </div>

                    <div className="h-3 rounded-full bg-gray-100 overflow-hidden">
                      <div
                        className={`h-full rounded-full ${budgetPercentage > 100 ? 'bg-red-500' : 'bg-blue-500'
                          }`}
                        style={{ width: `${Math.min(budgetPercentage, 100)}%` }}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Tasks Section */}
          <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm">

            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between mb-6">
              <div className="flex items-center gap-3">
              
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">Tasks</h2>
                  <p className="text-sm text-gray-500">{tasks.length} total tasks</p>
                </div>
              </div>

              <Link
                to={`/app/tasks/new?project_id=${id}`}
                className="inline-flex items-center justify-center gap-2  px-4 py-2.5 text-sm font-medium text-white transition"
              >
                <FiPlus />
                Add Task
              </Link>
            </div>

            {tasks.length === 0 ? (
              <div className="border border-dashed border-gray-300 rounded-2xl py-14 text-center">
                <div className="mx-auto w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center mb-4">
                  <FiCheckSquare className="text-2xl text-gray-400" />
                </div>

                <h3 className="text-lg font-semibold text-gray-900">No tasks yet</h3>
                <p className="text-gray-500 mt-1">
                  Create the first task to start tracking work.
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
                {tasks.slice(0, 6).map((task) => (
                  <Link
                    key={task.id}
                    to={`/app/tasks/${task.id}`}
                    className="group rounded-2xl border border-gray-200 bg-white p-5 hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <h3 className="font-semibold text-gray-900 line-clamp-2">
                        {task.title}
                      </h3>

                      {/* <span className="text-xs text-gray-400 whitespace-nowrap">
                        #{task.id}
                      </span> */}
                    </div>

                    <div
                      className="mt-3 text-sm text-gray-600 line-clamp-3 max-w-none"
                      dangerouslySetInnerHTML={{
                        __html: task.description || '<p>No description available.</p>',
                      }}
                    />

                    <div className="mt-4 flex flex-wrap gap-2">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusBadge(task.status)}`}>
                        {task.status}
                      </span>

                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${getPriorityBadge(task.priority)}`}>
                        {task.priority}
                      </span>
                    </div>

                    <div className="mt-5 pt-4 border-t border-gray-100 flex items-center justify-between">
                      <div className="flex items-center gap-3 min-w-0">
                        {task.assignee?.avatar_url ? (
                          <img
                            src={task.assignee.avatar_url}
                            alt={task.assignee.first_name}
                            className="w-9 h-9 rounded-full object-cover ring-2 ring-white shadow"
                          />
                        ) : (
                          <div className="w-9 h-9 rounded-full bg-indigo-600 text-white flex items-center justify-center text-sm font-semibold ring-2 ring-white shadow">
                            {task.assignee?.first_name?.charAt(0)?.toUpperCase() || 'U'}
                          </div>
                        )}

                        <div className="min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {task.assignee?.first_name || 'Unassigned'}
                          </p>
                          <p className="text-xs text-gray-500">Assignee</p>
                        </div>
                      </div>

                      <div className="text-right">
                        <p className="text-xs text-gray-500">Due date</p>
                        <p className="text-sm font-medium text-gray-900">
                          {customDateFormatShort(task.due_date) || 'No due date'}
                        </p>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}

            {tasks.length > 6 && (
              <div className="mt-6 text-center">
                <Link
                  to={`/app/tasks?project_id=${id}`}
                  className="inline-flex items-center gap-2 text-indigo-600 font-medium hover:text-indigo-700"
                >
                  View all {tasks.length} tasks
                  <span aria-hidden>→</span>
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* <div className="project-detail-content">
        <div className="main-section">
          <div className="info-card">
            <h2>Project Information</h2>
            <div className="info-grid">
              <div className="info-item">
                <label>Status</label>
                <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadge(project.status)}`}>
                  {project.status}
                </span>
              </div>

              <div className="info-item">
                <label>Priority</label>
                <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getPriorityBadge(project.priority)}`}>
                  {project.priority}
                </span>
              </div>

              <div className="info-item">
                <label>Start Date</label>
                <span>
                  {project.start_date ? new Date(project.start_date).toLocaleDateString() : '-'}
                </span>
              </div>

              <div className="info-item">
                <label>End Date</label>
                <span>
                  {project.end_date ? new Date(project.end_date).toLocaleDateString() : '-'}
                </span>
              </div>

              {project.project_manager && (
                <div className="info-item">
                  <label>Project Manager</label>
                  <span>
                    {project.project_manager.first_name} {project.project_manager.last_name}
                  </span>
                </div>
              )}
            </div>

            {project.description && (
              <div className="description-section">
                <h3>Description</h3>
                <div
                  className="prose max-w-none"
                  dangerouslySetInnerHTML={{ __html: project.description }}
                />
              </div>
            )}
          </div>

          <div className="info-card">
            <h2>Progress</h2>
            <div className="progress-section">
              <div className="progress-header">
                <span>Overall Progress</span>
                <span className="progress-value">{project.progress_percentage}%</span>
              </div>
              <div className="progress-bar">
                <div
                  className="progress-fill"
                  style={{ width: `${project.progress_percentage}%` }}
                ></div>
              </div>
            </div>
          </div>

          <div className="info-card">
            <h2>Budget</h2>
            <div className="budget-grid">
              <div className="budget-item">
                <label>Allocated</label>
                <span className="budget-value">
                  ${project.budget_allocated?.toLocaleString() || '0'}
                </span>
              </div>
              <div className="budget-item">
                <label>Spent</label>
                <span className="budget-value spent">
                  ${project.budget_spent?.toLocaleString() || '0'}
                </span>
              </div>
              <div className="budget-item">
                <label>Remaining</label>
                <span className={`budget-value ${budgetRemaining < 0 ? 'over-budget' : ''}`}>
                  ${budgetRemaining?.toLocaleString() || '0'}
                </span>
              </div>
            </div>
            {project.budget_allocated > 0 && (
              <div className="budget-progress">
                <div className="progress-bar">
                  <div
                    className={`progress-fill ${budgetPercentage > 100 ? 'over-budget' : ''}`}
                    style={{ width: `${Math.min(budgetPercentage, 100)}%` }}
                  ></div>
                </div>
                <span className="budget-percentage">{budgetPercentage}% used</span>
              </div>
            )}
          </div>

          <div className="info-card">
            <div className="card-header">
              <h2>Tasks ({tasks.length})</h2>
              <Link to={`/app/tasks/new?project_id=${id}`} className="btn btn-sm btn-primary">
                <i className="fas fa-plus"></i> Add Task
              </Link>
            </div>
            {tasks.length === 0 ? (
              <p className="text-muted">No tasks yet</p>
            ) : (
              <div className="tasks-list">
                {tasks.slice(0, 5).map(task => (
                  <div key={task.id} className="task-item">
                    <Link to={`/app/tasks/${task.id}`} className="task-title">
                      {task.title}
                    </Link>
                    <div className="task-meta">
                      <span className={`badge badge-sm ${getStatusBadge(task.status)}`}>
                        {task.status}
                      </span>
                      <span className={`badge badge-sm ${getPriorityBadge(task.priority)}`}>
                        {task.priority}
                      </span>
                    </div>
                  </div>
                ))}
                {tasks.length > 5 && (
                  <Link to={`/app/tasks?project_id=${id}`} className="view-all-link">
                    View all {tasks.length} tasks →
                  </Link>
                )}
              </div>
            )}
          </div>
        </div>
      </div> */}
    </div>





  );
};

export default ProjectDetail;


